/**
 * 組織レポート v3 のHTML生成。
 *
 * 【方針（2026-08-22 決定）】
 *  描画は前バージョン(teamReportGenerator.ts)の方式をそのまま使う。
 *  表紙・目次・中扉・ヘッダー・フッター・図は前バージョンの関数を再利用し、
 *  レポートテンプレートのCSS(/report-template/assets/css/style.css)を効かせる。
 *  こうすると共通ページは定義上まったく同じ見た目になる。
 *
 *  以前は React 部品＋独自SCSSで作り直していたが、細部を手で写す方式では
 *  差分が出続けたため取りやめた。算出ロジックと文言（orgScoreV3 ほか）は
 *  描画に依存しない純粋な関数なので、そのまま流用している。
 *
 * 【PDF】既存の generateTeamReportPdf をそのまま使う。
 *  キャプチャ用 iframe を 1440px 固定で描くため、版面は常に 950x670px に確定する。
 */
import {
  escapeHtml,
  generateConsultDeptPage,
  circlePath,
  generateOrgDiamondSvg,
  generateRadarPage,
  generateSectionTitlePage,
  generateTocPage,
  noteBlock,
  MEMBERS_PER_PAGE,
  type MemberScore,
  type OrgEvaluationInput,
  type TocSection,
} from './teamReportGenerator';
import { ORG_REPORT_NOTES } from './orgReportCopy';
import { GRADE_LABEL, SCORE_LABEL, SCORE_SUBLABEL } from '../../analysis/utils/orgScoreV3';
import { ORG_SHAPE_NOTE } from '../../analysis/utils/orgTypeV3';
import type { AxisKey } from '../../analysis/utils/gb74Report';
import { AXIS_PERSON_LABEL } from '../../analysis/utils/orgDistributionV3';

/** 軸の表示名。 */
const AXIS_LABEL: Record<AxisKey, string> = { A: 'A 論理', B: 'B 着実', C: 'C 共感', D: 'D 創造' };
import { CONSULT_SHAPE_META, consultDeptSummary } from '../../analysis/utils/orgConsultV3';
import { DONT_DO_BY_THEME } from '../../analysis/utils/orgImprovementCopyV3';
import type { OrgReportV3Data } from '../../analysis/utils/buildOrgReportV3';
import type { ConsultDeptResultV3 } from '../../analysis/utils/orgConsultV3';

/** 本文ページの共通の器。前バージョンの各ページと同じ構造にする。 */
function page(
  sectionNumber: string,
  title: string,
  subtitle: string,
  body: string,
  pageNumber: number,
  note?: string,
  /** 本文の下に全幅で置く帯（結びのメッセージなど） */
  footerBand?: string
): string {
  return `
    <li class="c-report-single_list_item _bg-white">
      <div class="c-report-single_network" style="height:100%;display:flex;flex-direction:column;box-sizing:border-box;">
        <header class="c-report-single_header-team">
          <div class="c-report-single_header-team_inner">
            <hgroup class="c-report-single_header-team_hgroup">
              <p class="c-report-single_header-team_hgroup_num">${escapeHtml(sectionNumber)}</p>
              <h3 class="c-report-single_header-team_hgroup_ttl">${escapeHtml(title)}</h3>
              <p class="c-report-single_header-team_hgroup_txt">${escapeHtml(subtitle)}</p>
            </hgroup>
            <p class="c-report-single_header-team_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></p>
          </div>
        </header>
        <div style="position:relative;width:100%;flex:1;min-height:0;padding:0 3.5%;margin-top:1%;box-sizing:border-box;display:flex;gap:2%;align-items:stretch;overflow:hidden;">
          ${body}
        </div>
        ${footerBand ?? ''}
        ${note ? noteBlock(note) : ''}
        <footer class="c-report-single_footer-team">
          <p class="c-report-single_footer-team_copy"><small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small></p>
          <p class="c-report-single_footer-team_num">${String(pageNumber).padStart(2, '0')}</p>
        </footer>
      </div>
    </li>`;
}

/** v3 の算出結果を、前バージョンのページ関数が受け取る形へ変換する。 */
function toEvaluationInput(data: OrgReportV3Data): OrgEvaluationInput {
  const { scores, texts, type, axisMeans } = data;
  const lead = [texts.catch?.見出し, texts.catch?.本文].filter(Boolean).join(' ');
  const weak = texts.score[scores.weakest];
  const weakLead = weak
    ? `いま最も低いのは「${SCORE_LABEL[scores.weakest]}」です。${weak.見出し ?? ''} ${weak.本文 ?? ''}`
    : '';
  return {
    totalScore: scores.gbScore,
    totalGrade: scores.grade,
    evaluationLead: `${lead}\n${weakLead}`.trim(),
    gauges: (['wellbeing', 'connection', 'retention'] as const).map((k) => {
      const s =
        k === 'connection'
          ? scores.connection
          : k === 'wellbeing'
            ? scores.wellbeing
            : scores.retention;
      return { label: SCORE_LABEL[k], score: s.score, grade: s.grade };
    }),
    orgTypeCode: type.code,
    orgTypeName: type.shape ? `${type.code}（${type.shape}）` : type.code,
    orgTypeDesc: [
      texts.shape?.本文,
      texts.typeCode?.本文,
      type.isChanceLevel
        ? '※ 回答者数が少ないため、この型は偶然の範囲と区別できません。参考値としてご覧ください。'
        : '',
      texts.tiltNote?.本文 ? `※ ${texts.tiltNote.本文}` : '',
    ]
      .filter(Boolean)
      .join('\n'),
    // 図のラベルにそのまま出るため、ここで小数点以下1位に丸める
    axis: {
      A: round1(axisMeans.A),
      B: round1(axisMeans.B),
      C: round1(axisMeans.C),
      D: round1(axisMeans.D),
    },
    scatter: data.scatter.points.map((p) => ({
      // 前バージョンの散布図は 0〜1 の正規化座標を受け取る。
      // v3 の座標式（横 x=(C+D)-(A+B) / 縦 y=(A+D)-(B+C)、範囲±24）を写す。
      x: 0.5 + p.x / 48,
      y: 0.5 - p.y / 48,
      axis: p.axis,
    })),
    distributionLead: data.distributionLead,
  };
}

/** レポート共通の青。図と揃える。 */
const BLUE = '#0066B7';

/**
 * 1-1 組織スコア（v3）。
 *
 * 右側を「3スコアそれぞれに説明ボックスを添える」形にする（score.png）。
 * モックは最低スコアを赤で強調しているが、レポート全体の配色に合わせて青にし、
 * 最低スコアだけ濃い青で示す。
 *
 * DBの 02-RULE-1つ は「内訳を出すのは最低スコア1つだけ」としているが、
 * これは つながりの4変数の内訳 の話。3スコアの説明文はモックどおり3つとも出す。
 */
function orgScorePageV3(data: OrgReportV3Data, pageNumber: number): string {
  const { scores, texts } = data;
  const keys = ['wellbeing', 'connection', 'retention'] as const;
  const rows = keys
    .map((k) => {
      const s =
        k === 'connection'
          ? scores.connection
          : k === 'wellbeing'
            ? scores.wellbeing
            : scores.retention;
      const weakest = scores.weakest === k;
      const bar = weakest ? BLUE : '#9CC3E3';
      const boxBg = weakest ? '#EAF3FA' : '#F5F7FA';
      const boxColor = weakest ? '#0B4A78' : '#4A5568';
      const t = texts.score[k];
      return `
        <div style="margin-bottom:10px;">
          <div style="display:flex;align-items:flex-start;gap:8px;">
            <span style="width:112px;flex-shrink:0;">
              <b style="font-size:12.5px;${weakest ? `color:${BLUE};` : ''}">${escapeHtml(SCORE_LABEL[k])}</b>
              <small style="display:block;font-size:9.5px;color:#888;line-height:1.5;margin-top:2px;">${escapeHtml(SCORE_SUBLABEL[k])}</small>
            </span>
            <span style="flex:1;min-width:0;">
              <span style="display:flex;justify-content:space-between;align-items:baseline;">
                <b style="font-size:12px;color:${bar};">${escapeHtml(s.grade)}</b>
                <span style="font-size:11.5px;color:#666;">${s.score}点</span>
              </span>
              <span style="display:block;height:9px;background:#EDF0F4;border-radius:5px;overflow:hidden;margin-top:3px;">
                <span style="display:block;height:100%;width:${s.score}%;background:${bar};border-radius:5px;"></span>
              </span>
            </span>
          </div>
          ${
            t
              ? `<p style="margin:6px 0 0 120px;padding:8px 11px;background:${boxBg};border-radius:8px;font-size:11px;line-height:1.75;color:${boxColor};">${escapeHtml(t.本文 ?? '')}</p>`
              : ''
          }
        </div>`;
    })
    .join('');

  const body = `
    <div style="flex:0 0 26%;min-width:0;text-align:center;background:#F4F8FC;border-radius:10px;padding:3% 1.5%;box-sizing:border-box;align-self:center;">
      <p style="font-size:11px;color:#666;margin:0 0 6px;">GB組織スコア</p>
      <p style="font-size:46px;font-weight:700;color:${BLUE};line-height:1.1;margin:0;">${scores.gbScore}<span style="font-size:14px;font-weight:400;">点</span></p>
      <p style="font-size:13px;margin:6px 0 0;">評価 ${escapeHtml(scores.grade)} ─ ${escapeHtml(GRADE_LABEL[scores.grade])}</p>
      <p style="font-size:10px;color:#888;margin:8px 0 0;line-height:1.6;">3つの評価の平均<br>評価は最も低い1つで決まります</p>
    </div>
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
      ${rows}
      <p style="font-size:10px;color:#888;margin:2px 0 0;">A（80〜100）良好 ／ B（60〜79）注意 ／ C（40〜59）要対応 ／ D（0〜39）緊急</p>
    </div>`;
  return page('1-1', '組織の評価', '組織スコア', body, pageNumber, ORG_REPORT_NOTES.orgScore);
}

/** 小数点以下1位に丸める。 */
function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

/**
 * 1-2 組織タイプ（v3）。
 *
 * 右側を type.png の要領にする。優勢率のバー4本、型の3択、型の説明の順。
 * モックは軸ごとに色を分けているが、レポート全体の配色に合わせて4本とも青にする。
 * 順位はバーの長さで分かる。
 *
 * 型の名称は「二極型／集中型／分散型」（仕様書・DB14の正式名称）。
 * モックは「バランス型」と書いているが、他ページの文言と揃うようDB側を採る。
 */
function orgTypePageV3(
  data: OrgReportV3Data,
  evalData: OrgEvaluationInput,
  pageNumber: number
): string {
  const { type, texts } = data;
  const order: AxisKey[] = (['A', 'B', 'C', 'D'] as AxisKey[])
    .slice()
    .sort((a, b) => type.rates[b] - type.rates[a]);
  const bars = order
    .map((k) => {
      const pct = Math.round(type.rates[k] * 100);
      const n = Math.round(type.rates[k] * type.memberCount);
      return `
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:7px;">
          <span style="width:58px;font-size:12px;font-weight:700;color:${BLUE};">${AXIS_LABEL[k]}</span>
          <span style="flex:1;height:11px;background:#EDF0F4;border-radius:6px;overflow:hidden;display:inline-block;">
            <span style="display:block;height:100%;width:${pct}%;background:${BLUE};border-radius:6px;"></span>
          </span>
          <span style="width:80px;text-align:right;font-size:11px;color:#666;">${pct}%（${n}名）</span>
        </div>`;
    })
    .join('');

  // 型は6区分（象限分布による統一判定）。該当するものを枠で強調する。
  const shapes: Array<[string, string]> = (
    ['二極型', '同系統型', '集中型', '中心集中型', '分散型', '中間型'] as const
  ).map((k) => [k, ORG_SHAPE_NOTE[k]]);
  const cards = shapes
    .map(([name, note]) => {
      const on = type.shape === name;
      return `<div style="flex:1 1 30%;min-width:0;border:${on ? `2px solid ${BLUE}` : '1px solid #E7EAEE'};border-radius:9px;padding:6px 4px;text-align:center;background:${on ? '#F4F8FC' : '#fff'};">
          <p style="margin:0;font-size:11px;font-weight:700;color:${on ? BLUE : '#4A5568'};">${escapeHtml(name)}</p>
          <p style="margin:1px 0 0;font-size:9.5px;color:#888;line-height:1.45;">${escapeHtml(note)}</p>
        </div>`;
    })
    .join('');

  const notes = [
    '※ 割合の合計は100%になりません。2つの持ち味が同時に強い方がいるためです。',
    '※ タイプに優劣はありません。どの型にも強みと、気をつける点があります。',
    type.isChanceLevel
      ? '※ 回答者数が少ないため、この型は偶然の範囲と区別できません。参考値としてご覧ください。'
      : '',
    texts.tiltNote?.本文 ? `※ ${texts.tiltNote.本文}` : '',
  ]
    .filter(Boolean)
    .join('<br>');

  const body = `
    <div style="flex:0 0 42%;min-width:0;text-align:center;display:flex;flex-direction:column;justify-content:center;">
      ${evalData.axis ? `<div style="flex:1;min-height:0;display:flex;align-items:center;justify-content:center;">${generateOrgDiamondSvg(evalData.axis)}</div>` : ''}
      <p style="font-size:10px;color:#888;line-height:1.7;margin:6px 0 0;text-align:left;">
        ※ 4軸の合計は24点で固定です。一人ひとりが尖っていても、組織平均では打ち消し合って中央に寄ります。<br>
        ※ 組織の特徴は、右の優勢率（その持ち味が強く出ている社員の割合）でご覧ください。
      </p>
    </div>
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
      <p style="font-size:14px;font-weight:700;margin:0 0 4px;">GB組織タイプ「${escapeHtml(type.code)}」${type.shape ? ` ＝ ${escapeHtml(type.shape)}` : ''}</p>
      <p style="font-size:11px;color:#666;margin:0 0 10px;line-height:1.6;">左のダイヤモンドがどう成り立っているか。その持ち味が強く出ている社員の割合です</p>
      ${bars}
      <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;">${cards}</div>
      ${
        texts.shape?.本文
          ? `<div style="margin-top:10px;padding:10px 12px;background:#EAF3FA;border-radius:8px;">
               <p style="margin:0 0 4px;font-size:12px;font-weight:700;color:#0B4A78;">御社は${escapeHtml(type.shape ?? '判定なし')}です</p>
               <p style="margin:0;font-size:11px;line-height:1.8;color:#0B4A78;">${escapeHtml(texts.shape.本文)}</p>
             </div>`
          : ''
      }
      <p style="font-size:10px;color:#888;margin-top:8px;line-height:1.7;">${notes}</p>
    </div>`;
  return page('1-2', '組織の評価', '組織タイプ', body, pageNumber, ORG_REPORT_NOTES.orgType);
}

/**
 * 社員の分布の図。前バージョン(generateScatterPage)と同じ描き方を写している。
 * 同心円＋十字を敷き、各社員を本人の4軸で描くミニ・ダイヤで置く。
 * 位置は 0〜1 に正規化した座標（v3 の x=(C+D)-(A+B) / y=(A+D)-(B+C) を写したもの）。
 */
function scatterSvgV3(scatter: NonNullable<OrgEvaluationInput['scatter']>): string {
  const W = 430;
  const H = 420;
  const CX = W / 2;
  const CY = H / 2;
  const RING_R = Math.min(W, H) / 2 - 26;
  const rings = [0.25, 0.5, 0.75, 1]
    .map((f) => circlePath(CX, CY, RING_R * f, 'fill="none" stroke="#D8E4EF" stroke-width="1"'))
    .join('');
  const cross =
    `<line x1="0" y1="${CY}" x2="${W}" y2="${CY}" stroke="#D8E4EF" stroke-width="1"/>` +
    `<line x1="${CX}" y1="0" x2="${CX}" y2="${H}" stroke="#D8E4EF" stroke-width="1"/>`;
  const MAXV = 12;
  const SIZE = 11;
  const DEG = { A: 135, D: 45, C: 315, B: 225 } as const;
  const dots = scatter
    .map((d) => {
      const px = Math.max(0, Math.min(1, d.x)) * W;
      const py = Math.max(0, Math.min(1, d.y)) * H;
      if (!d.axis)
        return circlePath(
          px,
          py,
          4.5,
          `fill="rgba(0,106,189,0.55)" stroke="${BLUE}" stroke-width="1"`
        );
      const pts = (['A', 'D', 'C', 'B'] as const)
        .map((k) => {
          const rad = (DEG[k] * Math.PI) / 180;
          const r = (Math.max(0, Math.min(MAXV, d.axis![k])) / MAXV) * SIZE;
          return `${(px + r * Math.cos(rad)).toFixed(1)},${(py - r * Math.sin(rad)).toFixed(1)}`;
        })
        .join(' ');
      return `<polygon points="${pts}" fill="rgba(0,106,189,0.35)" stroke="${BLUE}" stroke-width="1.2" stroke-linejoin="round"/>`;
    })
    .join('');
  const label = (x: number, y: number, anchor: string, text: string) =>
    `<text x="${x}" y="${y}" font-size="12" font-weight="700" fill="${BLUE}" text-anchor="${anchor}">${text}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" style="display:block;width:100%;max-width:430px;margin:0 auto;" preserveAspectRatio="xMidYMid meet">
      <rect x="0.5" y="0.5" width="${W - 1}" height="${H - 1}" rx="8" fill="#FAFBFC" stroke="#E7EAEE" stroke-width="1"/>
      ${rings}${cross}
      ${label(14, 20, 'start', 'A 論理')}
      ${label(W - 14, 20, 'end', 'D 創造')}
      ${label(W - 14, H - 10, 'end', 'C 共感')}
      ${label(14, H - 10, 'start', 'B 着実')}
      ${dots}
    </svg>`;
}

/**
 * 1-3 社員の分布（v3）。
 *
 * 右側を dist.png の要領にする。DB04 の見出しと本文に、実測の人数を添える。
 * 前バージョンの 12原型の文言（distributionLead）はここでは使わない。
 */
function distributionPageV3(
  data: OrgReportV3Data,
  evalData: OrgEvaluationInput,
  pageNumber: number
): string {
  const d = data.distribution;
  const n = d.memberCount;

  // 実際の内訳を必ず数字で示す。DB04の見出しだけだと、
  // 2象限に寄っている組織にも「四方に散らばっている」と出てしまうため。
  const AX: AxisKey[] = ['A', 'B', 'C', 'D'];
  const placed = AX.map((k) => ({ k, c: d.counts[k] }))
    .filter((x) => x.c > 0)
    .sort((a, b) => b.c - a.c);
  const listed = placed
    .slice(0, d.leaning ? 2 : 3)
    .map((x) => `${AXIS_PERSON_LABEL[x.k]}が${x.c}名`)
    .join('、');
  const others =
    n - placed.slice(0, d.leaning ? 2 : 3).reduce((sum, x) => sum + x.c, 0) - d.counts.center;
  const facts = [
    listed ? `${listed}。` : '',
    d.counts.center > 0 ? `中央付近が${d.counts.center}名。` : '',
    others > 0 ? `その他が${others}名です。` : '',
    d.leaning
      ? `${AXIS_LABEL[d.leaning[0]]}と${AXIS_LABEL[d.leaning[1]]}は隣り合う位置にあり、この2つに寄っています。`
      : '',
    `タイプが「${data.type.code}」になったのは、この散らばり方の結果です。`,
  ]
    .filter(Boolean)
    .join('');

  const body = `
    <div style="flex:0 0 52%;min-width:0;display:flex;align-items:center;justify-content:center;">
      ${scatterSvgV3(evalData.scatter ?? [])}
    </div>
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
      <div style="padding:12px 14px;background:#EAF3FA;border-radius:8px;">
        <p style="margin:0 0 6px;font-size:13px;font-weight:700;color:#0B4A78;">${escapeHtml(d.text?.見出し ?? '')}</p>
        <p style="margin:0;font-size:11.5px;line-height:1.9;color:#0B4A78;">${escapeHtml(facts)}</p>
        <p style="margin:8px 0 0;font-size:11.5px;line-height:1.9;color:#0B4A78;">${escapeHtml(d.text?.本文 ?? '')}</p>
      </div>
      <p style="font-size:10.5px;color:#888;margin-top:12px;line-height:1.8;">
        ※ ひとつのひし形が、社員1名の持ち味の形です。とがっている方向が、その方の強く出ている持ち味です。<br>
        ※ 位置にも形にも優劣はありません。お名前は表示していません。<br>
        ※ 合計 ${n} 名。
      </p>
    </div>`;
  return page(
    '1-3',
    '社員の分布',
    '一人ひとりの思考タイプの広がり',
    body,
    pageNumber,
    ORG_REPORT_NOTES.distribution
  );
}

/** 2-2 つながりの構造（部署ごとの一覧・v3で新設）。 */
function consultListPage(data: OrgReportV3Data, pageNumber: number): string {
  const rows = data.consult
    .map((d) => {
      const meta = d.shape ? CONSULT_SHAPE_META[d.shape] : null;
      const mark = meta ? meta.mark : '—';
      const color = meta ? meta.color : '#8A94A2';
      const state = meta ? meta.label : '判定なし（人数が少ないため）';
      const top = d.topName
        ? `${escapeHtml(d.topName)}さんに ${d.maxInDegree}名（${Math.round(d.maxRate * 100)}%）`
        : '—';
      return `
        <div style="display:flex;align-items:center;gap:12px;padding:12px 14px;border:1px solid #E7EAEE;border-radius:10px;margin-bottom:8px;background:#fff;">
          <span style="width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;color:${color};background:${color}1A;flex-shrink:0;">${mark}</span>
          <span style="width:150px;flex-shrink:0;line-height:1.5;"><b style="font-size:13px;display:block;">${escapeHtml(d.dept)}</b><small style="font-size:10.5px;color:#888;display:block;margin-top:4px;">回答 ${d.respondents}名</small></span>
          <span style="flex:1;font-size:12px;font-weight:700;color:${color};">${escapeHtml(state)}</span>
          <span style="font-size:11.5px;color:#4A5568;white-space:nowrap;">${top}</span>
        </div>`;
    })
    .join('');
  const body = `
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">
      ${rows}
      <p style="font-size:11px;color:#888;margin-top:10px;line-height:1.8;">
        ※ 相談は部署の中で起きます。全社をまとめると部署ごとの偏りが平均化されて見えなくなるため、部署ごとに出しています。<br>
        ※ 7名未満の部署は、偶然と区別できないため判定を行わず、観測値のみを表示しています。
      </p>
    </div>`;
  return page(
    '2-2',
    'つながりの構造',
    '部署ごとに、相談がどう流れているか',
    body,
    pageNumber,
    ORG_REPORT_NOTES.connection
  );
}

/**
 * 3-1 組織評価を上げる（v3）。
 *
 * 【構成（2026-08-23 指示）】1ページに集約する。
 *  左 … 今月やること1つ（取り組み／具体的な進め方／やってはいけないこと／スコアの期待値）
 *  右 … 余力があれば考えたいこと（タイプと分布からの示唆／組織の持ち味から3案）
 *  下 … 90日続けることを促す帯
 * 以前は右に連鎖（変数→スコア→GBスコア）の内訳を出し、続きを 3-2 に分けていたが、
 * 社長が見るのは結局「いくつになるか」なので、期待値の1行に置き換えて1ページにまとめた。
 */
function improvementPageV3(data: OrgReportV3Data, pageNumber: number): string {
  const im = data.improvement;

  // 具体的な進め方は算出側（orgImprovementV3）で組み立て済み。モバイル版と同じ値を使う。
  const howTo = im.howTo;

  /**
   * 見出し。左右のすべての見出しでこれを使う。
   * 以前は右の「もし、余力があったら〜」だけ青ベタの帯で、そこだけが強く見えていた。
   */
  const heading = (text: string) =>
    `<p style="font-size:12px;font-weight:700;color:${BLUE};margin:0 0 4px;">${escapeHtml(text)}</p>`;

  /**
   * 左右のカラム。
   *
   * 【高さを合わせる】justify-content:space-between で、上端と下端を左右でそろえる。
   * 余った高さはブロックとブロックの間へ均等に配る（gap が最低幅）。
   * 中身は必ず「見出し＋本文」を1つの div にまとめること。ばらすと space-between が
   * 見出しと本文の間も広げてしまい、対応関係が読めなくなる。
   */
  const column = (flex: string, blocks: string) =>
    `<div style="flex:${flex};min-width:0;display:flex;flex-direction:column;justify-content:space-between;gap:10px;">${blocks}</div>`;

  // ===== 左: 今月やること =====
  const outlook = im.gbOutlook;
  const left = column(
    '0 0 52%',
    `
      <div>
        <p style="margin:0 0 8px;padding:12px 14px;background:#EAF3FA;border-left:3px solid ${BLUE};border-radius:4px;font-size:14px;font-weight:700;line-height:1.7;">${escapeHtml(im.text?.取り組み ?? '')}</p>
        <p style="font-size:12px;line-height:1.9;margin:0;">${escapeHtml(im.text?.理由 ?? '')}</p>
      </div>
      ${
        howTo
          ? `<div>
               ${heading('具体的な進め方')}
               <p style="font-size:12px;line-height:1.9;margin:0;">${escapeHtml(howTo)}</p>
             </div>`
          : ''
      }
      <div>
        ${heading('やってはいけないこと')}
        <p style="margin:0;padding:10px 12px;background:#F4F8FC;border-left:3px solid ${BLUE};border-radius:4px;font-size:11.5px;line-height:1.8;color:#0B4A78;">${escapeHtml(DONT_DO_BY_THEME[im.theme])}</p>
      </div>
      <div>
        ${heading('GB組織スコアへの期待値')}
        <div style="display:flex;align-items:baseline;gap:10px;background:#F6F8FA;border-radius:9px;padding:11px 14px;">
          <span style="font-size:12px;color:#4A5568;">現状</span>
          <span style="font-size:20px;font-weight:700;color:#4A5568;">${outlook.now}</span>
          <span style="font-size:15px;color:#9AA6B2;">→</span>
          <span style="font-size:12px;color:${BLUE};">期待値</span>
          <span style="font-size:24px;font-weight:700;color:${BLUE};">${outlook.target}</span>
        </div>
        <p style="font-size:10.5px;color:#888;margin:6px 0 0;line-height:1.7;">※ 「最も低い項目が次の段階まで上がったら、他がそのままなら全体はどうなるか」を計算した目安です。実際の変化は状況により異なります。</p>
      </div>`
  );

  // ===== 右: 余力があれば =====
  const cohesionAction =
    data.cohesion.text?.次の一手 && data.cohesion.text.次の一手 !== '—'
      ? data.cohesion.text.次の一手
      : '';

  /**
   * 12原型の提案。
   *
   * 間隔は 12px の固定。以前は余った高さを3件へ均等に配っていたが、
   * 余りが大きいときに3件が離れすぎて間延びして見えた。
   * 余りはこのパート全体を下げるのに使う（下の margin-top:auto）。
   * 件数は通常3件だが、文言の取得に失敗したときは1件になることがある。
   */
  const archetype = data.archetypeActions
    .map(
      (a) => `
      <div>
        <p style="margin:0;font-size:11.5px;font-weight:700;">${escapeHtml(a.title)}</p>
        <p style="margin:2px 0 0;font-size:11px;line-height:1.75;color:#4A5568;">${escapeHtml(a.body)}</p>
      </div>`
    )
    .join('');

  const subBar = (text: string) =>
    `<p style="margin:0 0 6px;font-size:11.5px;font-weight:700;color:#0B4A78;border-bottom:1px solid #E7EAEE;padding-bottom:3px;">${escapeHtml(text)}</p>`;

  /**
   * 「GB組織タイプ／社員の分布から」の中身。
   *
   * 1行目 次の一手（何をするか）
   * 2行目 空き（1行ぶん）
   * 3行目以降 なぜそうなっているか（DB03の本文＋修飾句）
   *
   * 【箱をやめた理由（2026-08-23 指摘）】以前はここに「組織の噛み合わせ」の見出しと
   * 状態・指標の箱（4行）を置いていたが、そのぶん右カラムが伸びて、会社によっては
   * 「組織の持ち味から」の3件目が入りきらなかった。指標そのものは判定の内訳で、
   * 社長が読むのは結論（次の一手）と理由なので、箱ごと落として説明に寄せている。
   * 空きの1行は、次の一手と理由を読み分けるための区切り。
   */
  const c = data.cohesion;
  const cohesionDetail = `
    ${
      cohesionAction
        ? `<p style="margin:0;font-size:11.5px;line-height:1.8;">${escapeHtml(cohesionAction)}</p>`
        : ''
    }
    ${
      c.text?.本文
        ? `<p style="margin:19px 0 0;font-size:11px;line-height:1.75;">${escapeHtml(c.text.本文)}</p>`
        : ''
    }
    ${c.modifiers
      .map(
        (m) =>
          `<p style="margin:5px 0 0;font-size:11px;line-height:1.75;color:#4A5568;">${escapeHtml(m.本文 ?? '')}</p>`
      )
      .join('')}`;

  /**
   * 右カラム。上半分と下半分に分ける。
   *
   * 【なぜ半分で区切るか（2026-08-23 指示）】以前は余った高さをすべて
   * 「組織の持ち味から」の上へ逃がしていたため、空きの大きさが会社ごとに変わり、
   * 3つの提案は下端で12px間隔のまま詰まって見えていた。
   * 「組織の持ち味から」を必ず上下の真ん中から始めることで、
   *  ・どの会社のレポートでも同じ位置から始まる（前回と見比べやすい）
   *  ・下半分が取り分になるので、3つの提案の間隔を広げられる
   *
   * 上半分は flex:0 1 50%（伸びないが縮む）。噛み合わせの説明が長い会社では
   * 下半分が半分を超えるので、そのときは上半分が譲って溢れを防ぐ。
   *
   * 提案の間隔に上限を置いていないのは、下半分の高さが3件ぶんとほぼ同じで、
   * 広がる余地がもともと小さいため（以前の「間延び」は余りを全部渡していたのが原因）。
   * 1〜2件しか出ないとき（文言の取得に失敗した場合）は離れすぎるので上詰めにする。
   */
  const spreadItems = data.archetypeActions.length >= 3;
  const right = `
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;">
      <div style="flex:0 1 50%;min-height:0;display:flex;flex-direction:column;gap:10px;">
        <div>${heading('もし、余力があったらあわせて考えたいこと')}</div>
        <div>
          ${subBar('GB組織タイプ／社員の分布から')}
          ${cohesionDetail}
        </div>
      </div>
      ${
        archetype
          ? `<div style="flex:1 1 auto;min-height:0;display:flex;flex-direction:column;">
               ${subBar('組織の持ち味から（どれか1つを試してみる）')}
               <div style="flex:1;min-height:0;display:flex;flex-direction:column;gap:12px;justify-content:${spreadItems ? 'space-between' : 'flex-start'};">${archetype}</div>
             </div>`
          : ''
      }
    </div>`;

  const band = `
    <div style="margin:1.5% 3.5% 0;padding:12px 16px;background:#EAF3FA;border:1px solid #BBD6EA;border-radius:8px;text-align:center;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#0B4A78;line-height:1.6;">まずは出来ること1つをしっかりと90日続けてみることを推奨いたします</p>
    </div>`;

  return page(
    '3-1',
    '組織評価を上げる',
    '今月やること、ひとつだけ',
    `${left}${right}`,
    pageNumber,
    undefined,
    band
  );
}

/** 2-1 つながりの構造とは（v3の5形状に合わせた解説）。 */
function connectionIntroPageV3(pageNumber: number): string {
  const cards = (['助け合い', '集まりぎみ', '一極集中', '希薄'] as const)
    .map((k) => {
      const m = CONSULT_SHAPE_META[k];
      return `<div style="flex:1;min-width:0;text-align:center;padding:0 1%;">
          <p style="margin:0;font-size:26px;font-weight:700;color:${m.color};font-family:monospace;">${m.mark}</p>
          <p style="margin:4px 0 3px;font-size:11px;font-weight:700;color:${m.color};">${escapeHtml(m.label)}</p>
          <p style="margin:0;font-size:10px;line-height:1.8;color:#444;">${escapeHtml(SHAPE_INTRO[k])}</p>
        </div>`;
    })
    .join('');
  const body = `
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;gap:12px;">
      <p style="margin:0;font-size:12px;line-height:1.9;">「仕事で困ったとき、誰に相談しますか」という問いへの回答から、部署内の相談のつながりを図にしたものです。組織図ではなく、実際に人が動いている関係を表します。</p>
      <p style="margin:0;padding:10px 14px;background:#EAF3FA;border-left:3px solid #0066B7;border-radius:4px;font-size:12.5px;line-height:1.8;">見るところは1つだけです。<b>相談が1人に集まっているか、それとも互いに相談し合えているか。</b></p>
      <div style="display:flex;gap:1.5%;align-items:flex-start;">${cards}</div>
      <ul style="margin:0;padding:9px 14px;background:#F5F7FA;border-radius:6px;list-style:none;">
        <li style="font-size:11px;line-height:1.75;"><span style="display:inline-block;min-width:104px;font-weight:700;color:#0066B7;">円</span>部署のメンバー1人を表します。数字はその方に相談している人数です。</li>
        <li style="font-size:11px;line-height:1.75;"><span style="display:inline-block;min-width:104px;font-weight:700;color:#0066B7;">色つきの大きな円</span>相談が集まっている人です。</li>
        <li style="font-size:11px;line-height:1.75;"><span style="display:inline-block;min-width:104px;font-weight:700;color:#0066B7;">線</span>相談のつながり。集まっている人へ向かう線は濃く表示されます。</li>
      </ul>
      <p style="margin:0;font-size:11.5px;line-height:1.9;color:#0066B7;font-weight:700;">目指す形は三角形です。1人に集まっているなら相談先をもう1人つくる、やりとりが少ないならきっかけをつくることが、次の一手になります。</p>
    </div>`;
  return page('2-1', 'つながりの構造とは', 'この図の読み方', body, pageNumber);
}

const SHAPE_INTRO: Record<string, string> = {
  助け合い: '3人が互いに相談し合えています。1人が不在でも支え合えるため、最も安定した形です。',
  集まりぎみ: '相談が1人に寄り始めています。今のうちにもう1人決めておくと安定します。',
  一極集中: '相談が1人に集まっています。その方が不在のときに仕事が止まりやすい状態です。',
  希薄: 'そもそも相談のやりとりが少ない状態です。抱え込みや孤立が起きやすくなります。',
};

/** v3 の部署結果を、前バージョンの部署ページが受け取る形へ変換する。 */
function toConsultDeptStat(d: ConsultDeptResultV3) {
  const shape: 'triangle' | 'vee' | 'spoke' =
    d.shape === '助け合い' ? 'triangle' : d.shape === '希薄' || d.shape === null ? 'spoke' : 'vee';
  return {
    dept: d.dept,
    members: d.members.map((m) => ({ id: m.id, name: m.name, anonLabel: m.name, dept: d.dept })),
    edges: d.edges,
    inDegree: d.inDegree,
    keypersonIds: d.topId ? [d.topId] : [],
    maxInDegree: d.maxInDegree,
    concentration: d.maxRate,
    shape,
    summary: consultDeptSummary(d),
  };
}

/** 組織レポート v3 のHTMLを組み立てる。 */
export function generateOrgReportV3Html(data: OrgReportV3Data, date: string): string {
  const evalInput = toEvaluationInput(data);
  const memberScores: MemberScore[] = data.members.map((m) => ({ name: m.name, scores: m.axis }));
  const memberPageCount = Math.ceil(memberScores.length / MEMBERS_PER_PAGE);

  // ===== ページ採番（DOMに並ぶ順で一括決定する） =====
  let pno = 1; // 目次 = 01
  const noSec1 = ++pno;
  const noScore = ++pno;
  const noType = ++pno;
  const noDist = ++pno;
  const noSec2 = ++pno;
  const noIntro = ++pno;
  const noConsultList = data.consult.length > 0 ? ++pno : 0;
  const deptNos = data.consult.map(() => ++pno);
  const noSec3 = ++pno;
  const noImprove = ++pno;
  const noSec4 = memberPageCount > 0 ? ++pno : 0;
  const memberNos = Array.from({ length: memberPageCount }, () => ++pno);

  const toc: TocSection[] = [
    {
      title: '組織の評価',
      entries: [
        { no: '1-1', title: '組織スコア', page: noScore },
        { no: '1-2', title: '組織タイプ', page: noType },
        { no: '1-3', title: '社員の分布', page: noDist },
      ],
    },
    {
      // 部署ページは部署数だけ増えるため、目次には代表の1行だけを出す
      // （個別に並べると目次が溢れる）。
      title: 'つながりの構造',
      entries: [
        { no: '2-1', title: 'つながりの構造とは', page: noIntro },
        ...(noConsultList
          ? [{ no: '2-2', title: '部署別つながりの構造', page: noConsultList }]
          : []),
      ],
    },
    {
      title: '評価を上げるために',
      entries: [{ no: '3-1', title: '組織評価を上げる', page: noImprove }],
    },
    // 「組織メンバーの各タイプ」は社員数だけページが増えるため目次には載せない。
    // 本文（中扉とレーダー一覧）はこれまでどおり出る。
  ];

  const memberPages = Array.from({ length: memberPageCount }, (_, i) =>
    generateRadarPage(
      memberScores.slice(i * MEMBERS_PER_PAGE, (i + 1) * MEMBERS_PER_PAGE),
      memberNos[i],
      data.companyName,
      memberNos[i],
      `4-${i + 1}`
    )
  ).join('\n');

  return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta name="format-detection" content="telephone=no">
  <title>${escapeHtml(date)} 組織診断結果</title>
  <link rel="stylesheet" href="/report-template/assets/css/style.css" media="all">
  <script src="/report-template/assets/js/main.js" defer></script>
</head>
<body>
<div class="l-all-wrap">
  <main class="l-main">
    <section class="c-sec-report">
      <div class="inner">
        <div class="c-report-single">
          <h2 class="c-report-single_label _team">${escapeHtml(date)} 組織診断結果</h2>
          <div class="c-report-single_inner">
            <ul class="c-report-single_list _team">
              <!-- 表紙 -->
              <li class="c-report-single_list_item _bg-white">
                <div class="c-report-single_organization">
                  <hgroup class="c-report-single_organization_hgroup">
                    <p class="c-report-single_organization_hgroup_company">${escapeHtml(data.companyName)} 御中</p>
                    <h3 class="c-report-single_organization_hgroup_ttl">
                      <span class="c-report-single_organization_hgroup_ttl_img"><img src="/report-template/assets/img/organization_ttl.svg" alt="組織レポート"></span>
                      <span class="c-report-single_organization_hgroup_ttl_logo"><img src="/report-template/assets/img/logo-soshiki.svg" alt="GB組織"></span>
                    </h3>
                    <p class="c-report-single_organization_hgroup_date">${escapeHtml(date)}</p>
                  </hgroup>
                  <picture class="c-report-single_organization_img">
                    <img src="/report-template/assets/img/organization_img.png" alt="">
                  </picture>
                </div>
              </li>

              <!-- 目次 -->
              ${generateTocPage(toc)}

              <!-- 1 組織の評価 -->
              ${generateSectionTitlePage(1, '組織の評価', noSec1)}
              ${orgScorePageV3(data, noScore)}
              ${orgTypePageV3(data, evalInput, noType)}
              ${distributionPageV3(data, evalInput, noDist)}

              <!-- 2 つながりの構造 -->
              ${generateSectionTitlePage(2, 'つながりの構造', noSec2)}
              ${connectionIntroPageV3(noIntro)}
              ${noConsultList ? consultListPage(data, noConsultList) : ''}
              ${data.consult
                .map((d, i) =>
                  generateConsultDeptPage(toConsultDeptStat(d), deptNos[i], `2-${i + 3}`)
                )
                .join('\n')}

              <!-- 3 評価を上げるために -->
              ${generateSectionTitlePage(3, '評価を上げるために', noSec3)}
              ${improvementPageV3(data, noImprove)}

              <!-- 4 組織メンバーの各タイプ -->
              ${noSec4 ? generateSectionTitlePage(4, '組織メンバーの各タイプ', noSec4) : ''}
              ${memberPages}
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>
</div>
</body>
</html>`;
}

/** 表示用に「1-2 組織タイプ」の説明で使う型の凡例（画面には出さないが参照用）。 */
export const ORG_SHAPE_LEGEND = ORG_SHAPE_NOTE;
export { SCORE_SUBLABEL, GRADE_LABEL };
